// Compose: pick a template/preset → fill variables → adjust text & display →
// choose audience → send now / schedule / save draft. Live preview on the side,
// English and Arabic one under the other.
import React, { useEffect, useMemo, useState } from "react";
import { Send, CalendarClock, Save, BookmarkPlus, Eye, RotateCcw, Pencil, Languages } from "lucide-react";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";
import BroadcastCard from "../../../components/Broadcasts/BroadcastCard";
import RuleBuilder from "./RuleBuilder";
import AudienceReach from "./AudienceReach";
import { Modal, Field, Toggle, Btn, Select, Segmented, DateTimeField, SectionCard, todayIso } from "./ui";
import { renderContent, findProblems, toLocalInput, fromLocalInput, formatWhen } from "../utils/render";
import { CATEGORIES, SEVERITIES, DISPLAY_MODES } from "../utils/labels";
import { newRule, toPayload, rulesReady, fromServerRules, rulesSummary } from "../utils/rules";

const EMPTY = {
  templateId: null,
  category: "GENERAL",
  titleEn: "",
  titleAr: "",
  bodyEn: "",
  bodyAr: "",
  variables: [],
  variableValues: {},
  severity: "INFO",
  displayMode: "BANNER",
  dismissible: true,
  requireAck: false,
  addToInbox: false,
  audienceMode: "custom",
  audienceId: "",
  rules: [newRule("ALL")],
  delivery: "now",
  scheduledAt: "",
  expiryMode: "none", // none | hours | at
  expiryHours: 24,
  expiresAt: "",
};

const fromTemplate = (tpl, prev) => ({
  ...EMPTY,
  // Keep the audience the superadmin already built unless the template brings one.
  audienceMode: tpl.defaultAudienceId ? "saved" : prev.audienceMode,
  audienceId: tpl.defaultAudienceId ? tpl.defaultAudienceId : prev.audienceId,
  rules: prev.rules,
  delivery: prev.delivery,
  scheduledAt: prev.scheduledAt,
  templateId: tpl.id,
  category: tpl.category,
  titleEn: tpl.titleEn || "",
  titleAr: tpl.titleAr || "",
  bodyEn: tpl.bodyEn || "",
  bodyAr: tpl.bodyAr || "",
  variables: Array.isArray(tpl.variables) ? tpl.variables : [],
  variableValues: {},
  severity: tpl.severity,
  displayMode: tpl.displayMode,
  dismissible: tpl.dismissible,
  requireAck: tpl.requireAck,
  addToInbox: tpl.addToInbox,
  expiryMode: tpl.defaultDurationHours ? "hours" : "none",
  expiryHours: tpl.defaultDurationHours || 24,
});

const fromBroadcast = (b) => ({
  ...EMPTY,
  editingId: b.id,
  templateId: b.templateId || null,
  category: b.category,
  titleEn: b.titleEn || "",
  titleAr: b.titleAr || "",
  bodyEn: b.bodyEn || "",
  bodyAr: b.bodyAr || "",
  severity: b.severity,
  displayMode: b.displayMode,
  dismissible: b.dismissible,
  requireAck: b.requireAck,
  addToInbox: b.addToInbox,
  audienceMode: "custom",
  rules: fromServerRules(b.rules),
  delivery: b.status === "SCHEDULED" ? "schedule" : "now",
  scheduledAt: toLocalInput(b.scheduledAt),
  expiryMode: b.expiresAt ? "at" : "none",
  expiresAt: toLocalInput(b.expiresAt),
});

const VariableInput = ({ def, value, onChange }) => {
  const { s } = useBroadcastPage();
  if (def.type === "datetime") {
    return <DateTimeField value={value ? toLocalInput(value) : ""} onChange={(v) => onChange(fromLocalInput(v) || "")} />;
  }
  return (
    <input
      type={def.type === "number" ? "number" : "text"}
      className="bc-input"
      style={s.input}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const ComposeTab = ({ seed, onDone }) => {
  const { s, t, theme, language, templates, audiences, options, notify, refreshTemplates } = useBroadcastPage();
  const c = t.compose;
  const [f, setF] = useState(EMPTY);
  const [reach, setReach] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'send' | 'schedule'
  const [presetModal, setPresetModal] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showArabic, setShowArabic] = useState(true);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const when = (d) => formatWhen(d, language);

  // Seeds from other tabs: "Use template" or "Edit" on a draft/scheduled broadcast.
  useEffect(() => {
    if (!seed) return;
    if (seed.template) setF((prev) => fromTemplate(seed.template, prev));
    if (seed.broadcast) setF(fromBroadcast(seed.broadcast));
  }, [seed]);

  const systemTemplates = templates.filter((x) => x.isSystem && x.isActive);
  const presets = templates.filter((x) => !x.isSystem && x.isActive);

  const rendered = useMemo(() => renderContent(f, f.variables, f.variableValues), [f]);
  const problems = useMemo(() => findProblems(f, f.variables, f.variableValues, language), [f, language]);

  const audienceReady = f.audienceMode === "saved" ? !!f.audienceId : rulesReady(f.rules);
  const sendAt = f.delivery === "schedule" && f.scheduledAt ? new Date(f.scheduledAt) : new Date();
  const computedExpiry =
    f.expiryMode === "hours"
      ? new Date(sendAt.getTime() + Number(f.expiryHours || 0) * 3600 * 1000)
      : f.expiryMode === "at" && f.expiresAt
      ? new Date(f.expiresAt)
      : null;

  const errors = [];
  if (!f.titleEn.trim()) errors.push(c.errTitleEn);
  if (!f.bodyEn.trim()) errors.push(c.errBodyEn);
  if (problems.missing.length) errors.push(c.errFillIn(problems.missing.join(t.rules.listSep)));
  if (problems.undefinedKeys.length) errors.push(c.errUnknownPlaceholders(problems.undefinedKeys.map((k) => `{{${k}}}`).join(t.rules.listSep)));
  if (!audienceReady) errors.push(c.errAudience);
  if (f.delivery === "schedule" && (!f.scheduledAt || new Date(f.scheduledAt) <= new Date())) errors.push(c.errFutureTime);
  if (computedExpiry && computedExpiry <= sendAt) errors.push(c.errExpiry);

  const buildPayload = (action) => ({
    templateId: f.templateId || null,
    audienceId: f.audienceMode === "saved" && f.audienceId ? Number(f.audienceId) : null,
    rules: f.audienceMode === "custom" ? toPayload(f.rules) : undefined,
    category: f.category,
    titleEn: f.titleEn,
    titleAr: f.titleAr || null,
    bodyEn: f.bodyEn,
    bodyAr: f.bodyAr || null,
    variables: f.variables,
    variableValues: Object.fromEntries(Object.entries(f.variableValues).filter(([, v]) => v !== "" && v != null)),
    severity: f.severity,
    displayMode: f.displayMode,
    dismissible: f.dismissible,
    requireAck: f.requireAck,
    addToInbox: f.addToInbox,
    action,
    scheduledAt: f.delivery === "schedule" ? fromLocalInput(f.scheduledAt) : null,
    expiresAt: computedExpiry ? computedExpiry.toISOString() : null,
  });

  const resetKeepingAudience = () =>
    setF((prev) => ({ ...EMPTY, rules: prev.rules, audienceMode: prev.audienceMode, audienceId: prev.audienceId }));

  const submit = async (action) => {
    setBusy(true);
    try {
      const payload = buildPayload(action);
      const result = f.editingId ? await broadcastApi.update(f.editingId, payload) : await broadcastApi.create(payload);
      const msg =
        action === "send"
          ? c.sentTo(result?.recipientCount ?? 0)
          : action === "schedule"
          ? c.scheduledFor(when(result?.scheduledAt))
          : c.draftSaved;
      setConfirm(null);
      resetKeepingAudience();
      onDone?.();
      notify("success", msg);
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const savePreset = async () => {
    if (!presetName.trim()) return;
    setBusy(true);
    try {
      await broadcastApi.createTemplate({
        name: presetName.trim(),
        category: f.category,
        titleEn: f.titleEn,
        titleAr: f.titleAr || null,
        bodyEn: f.bodyEn,
        bodyAr: f.bodyAr || null,
        variables: f.variables,
        severity: f.severity,
        displayMode: f.displayMode,
        dismissible: f.dismissible,
        requireAck: f.requireAck,
        addToInbox: f.addToInbox,
        defaultDurationHours: f.expiryMode === "hours" ? Number(f.expiryHours) || null : null,
        defaultAudienceId: f.audienceMode === "saved" && f.audienceId ? Number(f.audienceId) : null,
      });
      await refreshTemplates();
      notify("success", c.presetSaved(presetName.trim()));
      setPresetModal(false);
      setPresetName("");
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const previewBroadcast = {
    id: 0,
    ...rendered,
    severity: f.severity,
    displayMode: f.displayMode,
    dismissible: f.requireAck ? false : f.displayMode === "MODAL" ? true : f.dismissible,
    requireAck: f.requireAck,
  };
  const variant = { BANNER: "banner", TOAST: "toast", MODAL: "modal" }[f.displayMode];
  const hasArabic = !!(rendered.titleAr || rendered.bodyAr);
  const savedAudience = audiences.find((a) => String(a.id) === String(f.audienceId));
  const varLabel = (v) => (language === "ar" && v.labelAr) || v.labelEn;

  let step = 0;
  const nextStep = () => {
    step += 1;
    return step;
  };

  const templateChip = (tpl) => (
    <button key={tpl.id} type="button" className="bc-chip" style={s.pickChip(f.templateId === tpl.id)} onClick={() => setF((prev) => fromTemplate(tpl, prev))}>
      <span style={s.pickChipName}>{tpl.name}</span>
      <span style={s.pickChipMeta}>{t.categories[tpl.category]}</span>
    </button>
  );

  const previewPane = (lang) => (
    <div style={{ marginBottom: 16 }}>
      <div style={s.previewLabel}>
        <span>{lang === "en" ? c.english : c.arabic}</span>
      </div>
      <div style={{ ...s.previewStage(variant), justifyContent: variant === "toast" ? (lang === "ar" ? "flex-end" : "flex-start") : "center" }}>
        <div style={{ width: variant === "toast" ? "min(340px, 100%)" : "100%" }}>
          <BroadcastCard broadcast={previewBroadcast} language={lang} theme={theme} variant={variant} preview />
        </div>
      </div>
    </div>
  );

  return (
    <div className="bc-compose-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(320px, 1fr)", gap: 24, alignItems: "start" }}>
      <div style={{ minWidth: 0 }}>
        {f.editingId && (
          <div style={{ ...s.notice("info"), justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Pencil size={16} /> {c.editing(f.editingId)}
            </span>
            <Btn kind="secondary" small onClick={() => setF(EMPTY)}>
              <RotateCcw size={14} /> {c.startOver}
            </Btn>
          </div>
        )}

        {/* Start from */}
        <SectionCard
          step={nextStep()}
          title={c.stepTemplate}
          subtitle={c.stepTemplateSub}
          actions={
            f.templateId ? (
              <Btn kind="secondary" small onClick={resetKeepingAudience}>
                {c.clear}
              </Btn>
            ) : null
          }
        >
          <div style={s.chipGrid}>
            {systemTemplates.map(templateChip)}
          </div>
          {presets.length > 0 && (
            <>
              <div style={s.subLabel}>{c.yourPresets}</div>
              <div style={s.chipGrid}>
                {presets.map(templateChip)}
              </div>
            </>
          )}
        </SectionCard>

        {/* Details */}
        {f.variables.length > 0 && (
          <SectionCard step={nextStep()} title={c.stepDetails} subtitle={c.stepDetailsSub}>
            <div style={s.grid2}>
              {f.variables.map((v) => (
                <Field key={v.key} label={`${varLabel(v)}${v.required ? " *" : ""}`}>
                  <VariableInput def={v} value={f.variableValues[v.key]} onChange={(val) => set({ variableValues: { ...f.variableValues, [v.key]: val } })} />
                </Field>
              ))}
            </div>
            <p style={{ ...s.hint, marginTop: 0 }}>{c.detailsHint}</p>
          </SectionCard>
        )}

        {/* Message */}
        <SectionCard step={nextStep()} title={c.stepMessage} subtitle={c.stepMessageSub}>
          <Field label={`${c.titleEn} *`}>
            <input dir="ltr" className="bc-input" style={s.input} value={f.titleEn} maxLength={200} onChange={(e) => set({ titleEn: e.target.value })} />
          </Field>
          <Field label={`${c.bodyEn} *`}>
            <textarea dir="ltr" className="bc-input" style={s.textarea} value={f.bodyEn} maxLength={5000} onChange={(e) => set({ bodyEn: e.target.value })} />
          </Field>
          <Toggle checked={showArabic} onChange={setShowArabic} label={c.arabicVersion} hint={c.arabicVersionHint} />
          {showArabic && (
            <>
              <Field label={c.titleAr}>
                <input dir="rtl" className="bc-input" style={{ ...s.input, textAlign: "right" }} value={f.titleAr} maxLength={200} onChange={(e) => set({ titleAr: e.target.value })} />
              </Field>
              <Field label={c.bodyAr}>
                <textarea dir="rtl" className="bc-input" style={{ ...s.textarea, textAlign: "right" }} value={f.bodyAr} maxLength={5000} onChange={(e) => set({ bodyAr: e.target.value })} />
              </Field>
            </>
          )}
          <div style={s.grid2}>
            <Field label={c.category}>
              <Select value={f.category} onChange={(v) => set({ category: v })} options={CATEGORIES.map((k) => ({ value: k, label: t.categories[k] }))} />
            </Field>
            <Field label={c.severity}>
              <Select value={f.severity} onChange={(v) => set({ severity: v })} options={SEVERITIES.map((k) => ({ value: k, label: t.severities[k] }))} />
            </Field>
          </div>
          <Field label={c.displayAs} hint={t.displayModes[f.displayMode].hint}>
            <Segmented value={f.displayMode} onChange={(v) => set({ displayMode: v })} options={DISPLAY_MODES.map((m) => ({ value: m, label: t.displayModes[m].label }))} />
          </Field>
          <Toggle
            checked={f.requireAck}
            onChange={(v) => set({ requireAck: v, dismissible: v ? false : f.dismissible })}
            label={c.requireAck}
            hint={c.requireAckHint}
          />
          <Toggle
            checked={f.requireAck ? false : f.displayMode === "MODAL" ? true : f.dismissible}
            disabled={f.requireAck || f.displayMode === "MODAL"}
            onChange={(v) => set({ dismissible: v })}
            label={c.dismissible}
            hint={c.dismissibleHint}
          />
          <Toggle checked={f.addToInbox} onChange={(v) => set({ addToInbox: v })} label={c.addToInbox} hint={c.addToInboxHint} />
        </SectionCard>

        {/* Audience */}
        <SectionCard step={nextStep()} title={c.stepAudience} subtitle={c.stepAudienceSub}>
          <div style={{ marginBottom: 16 }}>
            <Segmented
              value={f.audienceMode}
              onChange={(v) => set({ audienceMode: v })}
              options={[
                { value: "custom", label: c.buildRules },
                { value: "saved", label: c.savedAudience },
              ]}
            />
          </div>
          {f.audienceMode === "saved" ? (
            <Field hint={savedAudience ? rulesSummary(savedAudience.rules, options, t) : audiences.length ? null : c.noSavedAudiences}>
              <Select
                value={f.audienceId}
                onChange={(v) => set({ audienceId: v })}
                placeholder={c.selectAudience}
                options={audiences.map((a) => ({ value: a.id, label: a.name }))}
              />
            </Field>
          ) : (
            <RuleBuilder rules={f.rules} onChange={(rules) => set({ rules })} />
          )}
          <AudienceReach
            ready={audienceReady}
            rules={f.audienceMode === "custom" ? toPayload(f.rules) : null}
            audienceId={f.audienceMode === "saved" ? Number(f.audienceId) : null}
            onCount={setReach}
          />
        </SectionCard>

        {/* Delivery */}
        <SectionCard step={nextStep()} title={c.stepDelivery} subtitle={c.stepDeliverySub}>
          <div style={s.grid2}>
            <Field label={c.send}>
              <Segmented
                value={f.delivery}
                onChange={(v) => set({ delivery: v })}
                options={[
                  { value: "now", label: c.now },
                  { value: "schedule", label: c.schedule },
                ]}
              />
              {f.delivery === "schedule" && (
                <div style={{ marginTop: 10 }}>
                  <DateTimeField value={f.scheduledAt} minDate={todayIso()} onChange={(v) => set({ scheduledAt: v })} />
                </div>
              )}
            </Field>
            <Field label={c.stopsShowing} hint={computedExpiry ? c.expiresHint(when(computedExpiry)) : c.expiryNoneHint}>
              <Select
                value={f.expiryMode}
                onChange={(v) => set({ expiryMode: v })}
                options={[
                  { value: "none", label: c.expiryNone },
                  { value: "hours", label: c.expiryHours },
                  { value: "at", label: c.expiryAt },
                ]}
              />
              {f.expiryMode === "hours" && (
                <div style={{ ...s.row, marginTop: 10, flexWrap: "nowrap" }}>
                  <input
                    type="number"
                    min={1}
                    max={8760}
                    className="bc-input"
                    style={s.input}
                    value={f.expiryHours}
                    onChange={(e) => set({ expiryHours: e.target.value })}
                  />
                  <span style={{ fontSize: 13.5, color: s.colors.muted, whiteSpace: "nowrap" }}>{c.hours}</span>
                </div>
              )}
              {f.expiryMode === "at" && (
                <div style={{ marginTop: 10 }}>
                  <DateTimeField value={f.expiresAt} minDate={todayIso()} defaultTime="23:59" onChange={(v) => set({ expiresAt: v })} />
                </div>
              )}
            </Field>
          </div>

          {errors.length > 0 && (
            <ul style={s.errorList}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}

          <div style={{ ...s.row, justifyContent: "space-between" }}>
            <div style={s.row}>
              <Btn kind="secondary" disabled={busy || !f.titleEn.trim() || !f.bodyEn.trim()} onClick={() => setPresetModal(true)}>
                <BookmarkPlus size={16} /> {c.saveAsPreset}
              </Btn>
              <Btn kind="secondary" disabled={busy || !f.titleEn.trim() || !f.bodyEn.trim() || !audienceReady} onClick={() => submit("draft")}>
                <Save size={16} /> {c.saveDraft}
              </Btn>
            </div>
            {f.delivery === "now" ? (
              <Btn kind="success" disabled={busy || errors.length > 0} onClick={() => setConfirm("send")}>
                <Send size={16} /> {c.sendNow}
              </Btn>
            ) : (
              <Btn disabled={busy || errors.length > 0} onClick={() => setConfirm("schedule")}>
                <CalendarClock size={16} /> {c.scheduleBtn}
              </Btn>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Preview — English then Arabic */}
      <div className="bc-compose-preview" style={{ position: "sticky", top: 16, minWidth: 0 }}>
        <SectionCard icon={Eye} title={c.preview} subtitle={c.previewSub}>
          {previewPane("en")}
          {hasArabic ? (
            previewPane("ar")
          ) : (
            <div style={{ ...s.notice("warning"), marginBottom: 16 }}>
              <Languages size={16} style={{ flexShrink: 0 }} />
              <span>{c.noArabicPreview}</span>
            </div>
          )}
          <div style={s.previewMeta}>
            <span style={s.chip}>{t.displayModes[f.displayMode].label}</span>
            <span style={s.chip}>{t.severities[f.severity]}</span>
            <span style={s.chip}>{t.categories[f.category]}</span>
            {reach != null && <span style={s.chip}>{c.recipients(reach)}</span>}
          </div>
        </SectionCard>
      </div>

      {confirm && (
        <Modal
          title={confirm === "send" ? c.confirmSendTitle : c.confirmScheduleTitle}
          icon={confirm === "send" ? Send : CalendarClock}
          onClose={() => setConfirm(null)}
          busy={busy}
          footer={
            <>
              <Btn kind="secondary" disabled={busy} onClick={() => setConfirm(null)}>
                {t.back}
              </Btn>
              <Btn kind={confirm === "send" ? "success" : "primary"} disabled={busy} onClick={() => submit(confirm)}>
                {confirm === "send" ? <Send size={16} /> : <CalendarClock size={16} />}
                {busy ? t.working : confirm === "send" ? c.sendToN(reach) : c.scheduleBtn}
              </Btn>
            </>
          }
        >
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: s.colors.text }}>{language === "ar" && rendered.titleAr ? rendered.titleAr : rendered.titleEn}</div>
            <div style={{ color: s.colors.muted }}>
              {confirm === "schedule" ? c.goesOutAt(when(fromLocalInput(f.scheduledAt))) : c.goesOutNow}
              {computedExpiry ? c.andExpires(when(computedExpiry)) : ""}
            </div>
            <div style={{ color: s.colors.muted }}>
              {c.audience}: {f.audienceMode === "saved" ? savedAudience?.name : rulesSummary(f.rules, options, t)}
              {reach != null ? ` (${reach.toLocaleString()})` : ""}
            </div>
            {!f.titleAr && !f.bodyAr && (
              <div style={{ ...s.notice("warning"), marginTop: 12 }}>
                <Languages size={16} style={{ flexShrink: 0 }} />
                <span>{c.noArabicWarning}</span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {presetModal && (
        <Modal
          title={c.saveAsPreset}
          icon={BookmarkPlus}
          onClose={() => setPresetModal(false)}
          busy={busy}
          footer={
            <>
              <Btn kind="secondary" disabled={busy} onClick={() => setPresetModal(false)}>
                {t.cancel}
              </Btn>
              <Btn disabled={busy || !presetName.trim()} onClick={savePreset}>
                <BookmarkPlus size={16} /> {busy ? t.saving : c.savePreset}
              </Btn>
            </>
          }
        >
          <Field label={c.presetName} hint={c.presetNameHint} style={{ marginBottom: 0 }}>
            <input
              autoFocus
              className="bc-input"
              style={s.input}
              value={presetName}
              maxLength={120}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && savePreset()}
            />
          </Field>
        </Modal>
      )}
    </div>
  );
};

export default ComposeTab;
